import { useQueryClient } from "@tanstack/react-query";

import { videoKey, useUpdateVideo } from "@app/hooks/videos";
import { useZoltarMutation } from "@app/hooks/zoltar";
import { parseZoltarGenerateAssistantJson } from "@app/services/zoltar";

import { useIsAdmin } from "@app/hooks/useRoles";

import BRollGeneric from "@app/data/b-roll/generic";
import BRollAdobeSpecific from "@app/data/b-roll/adobe-specific";

const bRollData = [
  ...(BRollGeneric?.items ?? []),
  ...(BRollAdobeSpecific?.items ?? []),
];

export const sceneKey = {
  all: ["videos"],
  scenes: () => [...videoKey.all, "scenes"],
  scene: (id) => [...videoKey.scenes(), id],
};

/**
 * Builds the Zoltar prompt template for scene generation from the current
 * video data (selected campaign script/scenes/colors) plus an optional hint.
 * @returns {{systemPromptId: string, template: Array<object>}} ID and user-only messages.
 */
export const getScenesPromptTemplate = (videoData, hint) => {
  const currentCampaign =
    videoData?.campaigns?.find(
      (c) => c.id === videoData.selectedCampaignIds?.[0],
    ) ?? {};

  const brandColor = videoData?.customer?.["Brand-color"];
  const secondaryColor = videoData?.customer?.["Secondary-color"];

  const template = [
    {
      role: "user",
      prompt_template: `[Customer]: ${videoData?.customer?.name}${
        brandColor
          ? `
[Brand-colors]: ${[brandColor, secondaryColor].filter(Boolean).join(", ")}`
          : ""
      }`,
    },
    ...(currentCampaign?.script
      ? [
          {
            role: "user",
            prompt_template: `[Script]:
${currentCampaign?.script}`,
          },
        ]
      : []),
    ...(currentCampaign?.scenes
      ? [
          {
            role: "user",
            prompt_template: `[Scenes]:
${JSON.stringify(currentCampaign?.scenes ?? [])}`,
          },
        ]
      : []),
  ];

  if (hint) {
    template.push({
      role: "user",
      prompt_template: `[Hints]:
${hint}`,
    });
  }

  return { systemPromptId: "PV-scenes", template };
};

/**
 * Scene generation, lifted out of `ScenesStep` so it can be instantiated once at
 * the wizard level: shared with `ScenesStep` (loading UI + manual "adjust"
 * regeneration) and triggered as the SCENES step's entering action by
 * `handleNextStep`.
 *
 * Returns:
 *   - `mutation`   — the underlying Zoltar mutation (isPending/isError/reset).
 *   - `regenerate(hint, setPrompt)` — trigger generation from the latest video
 *     data in the query cache; clears the prompt on success when provided.
 *   - `getScenesPromptTemplate` — prompt builder used by `EditPrompt` previews.
 */
export const useSceneGeneration = ({ videoId }) => {
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const updateVideo = useUpdateVideo(videoId);

  const mutation = useZoltarMutation(
    {
      mutationKey: sceneKey.scenes(videoId),
      onSuccess: (data, variables, onMutateResult, context) => {
        const videoData = context.client.getQueryData(videoKey.detail(videoId));
        const campaigns = videoData?.campaigns ?? [];
        const currentCampaign =
          campaigns?.find((c) => c.id === videoData.selectedCampaignIds?.[0]) ??
          {};

        const updatedCampaign = {
          ...currentCampaign,
          scenes: (data?.scenes ?? []).map((scene) => ({
            ...scene,
            Color: scene?.Video?.length ? ["#000000"] : scene?.Color,
          })),
        };

        if (data?.scenesPromptHints) {
          updatedCampaign.scenesPromptHints = data?.scenesPromptHints;
        }

        if (currentCampaign?.scenes?.length) {
          // If the current campaign already has scenes, generate a new ID for the updated campaign for versioning purposes.
          updatedCampaign.id = crypto.randomUUID();
        }

        const selectedCampaignIds = [updatedCampaign.id];

        const updatedCampaigns = [
          ...campaigns.filter((c) => c.id !== updatedCampaign.id),
          updatedCampaign,
        ].slice(-10);

        return updateVideo.mutateAsync({
          campaigns: updatedCampaigns,
          selectedCampaignIds,
        });
      },
      onError: (error, variables, onMutateResult, context) => {
        if (error?.cause?.data?.error) {
          context.meta = context.meta || {};
          context.meta.errorMessage = error?.cause?.data?.error;
        }
      },
    },
    (data) =>
      parseZoltarGenerateAssistantJson(data, "Failed to generate content"),
  );

  const regenerate = (hint = "", setPrompt) => {
    const videoData = queryClient.getQueryData(videoKey.detail(videoId));

    return mutation.mutateAsync(
      {
        promptTemplate: getScenesPromptTemplate(videoData, hint).template,
        systemPromptId: "PV-scenes",
        promptOverrideKey: isAdmin ? `customTemplate__${videoData?.templateId}-scenes` : undefined,
        model: "gpt-5.6-sol",
        skills: ["bestory-mongodb-search"],
      },
      {
        onSuccess: () => {
          if (setPrompt) {
            setPrompt("");
          }
        },
      },
    );
  };

  return { mutation, regenerate, getScenesPromptTemplate };
};
